import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import { 
  Globe, 
  Save, 
  RefreshCw,
  Image,
  Eye,
  Share2,
  CheckCircle,
  AlertCircle,
  Copy,
  ExternalLink,
  Facebook,
  Twitter,
  Linkedin,
  Upload,
  Info
} from 'lucide-react';

// SEO validation schema
const seoSchema = z.object({
  meta_title: z.string()
    .min(10, 'Meta title must be at least 10 characters')
    .max(60, 'Meta title should not exceed 60 characters'),
  meta_description: z.string()
    .min(50, 'Meta description must be at least 50 characters')
    .max(160, 'Meta description should not exceed 160 characters'),
  meta_keywords: z.string()
    .min(10, 'Meta keywords must be at least 10 characters')
    .max(255, 'Keywords should not exceed 255 characters'),
  social_banner_url: z.string().optional().or(z.literal('')),
});

// Helper function to convert file to base64
const convertFileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const SEOManagement = () => {
  const [loading, setLoading] = useState(false);
  const [seoSettings, setSeoSettings] = useState(null);
  const [activeTab, setActiveTab] = useState('settings');
  const [previewMode, setPreviewMode] = useState('google');

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset
  } = useForm({
    resolver: zodResolver(seoSchema),
    defaultValues: {
      meta_title: '',
      meta_description: '',
      meta_keywords: '',
      social_banner_url: ''
    }
  });

  const watchedTitle = watch('meta_title');
  const watchedDescription = watch('meta_description');
  const watchedKeywords = watch('meta_keywords');
  const watchedBanner = watch('social_banner_url');

  useEffect(() => {
    fetchSEOSettings();
  }, []);

  const fetchSEOSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/api/admin/seo-settings`, {
        withCredentials: true
      });
      setSeoSettings(response.data);
      reset({
        meta_title: response.data.meta_title || '',
        meta_description: response.data.meta_description || '',
        meta_keywords: response.data.meta_keywords || '',
        social_banner_url: response.data.social_banner_url || ''
      });
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error fetching SEO settings:', error);
        toast.error("Failed to fetch SEO settings");
      }
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_BASE}/api/admin/seo-settings`, data, {
        withCredentials: true
      });
      setSeoSettings(response.data);
      toast.success("SEO settings updated successfully");
    } catch (error) {
      console.error('Error updating SEO settings:', error);
      toast.error(error.response?.data?.detail || "Failed to update SEO settings");
    } finally {
      setLoading(false);
    }
  };

  // Social sharing URLs
  const generateSocialShareUrls = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
    const title = encodeURIComponent(watchedTitle || 'EduLegalHub - AI-Powered Legal & Educational Solutions');
    const description = encodeURIComponent(watchedDescription || 'Transform legal documents and education with AI');
    const url = encodeURIComponent(baseUrl);
    const image = encodeURIComponent(watchedBanner || '');

    return {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${title}`,
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}&via=EduLegalHub`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}&title=${title}&summary=${description}`,
      whatsapp: `https://wa.me/?text=${title}%20${url}`,
      telegram: `https://t.me/share/url?url=${url}&text=${title}`
    };
  };

  // Banner upload dropzone
  const BannerDropzone = () => {
    const [uploading, setUploading] = useState(false);
    
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      accept: {
        'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp']
      },
      maxFiles: 1,
      maxSize: 5 * 1024 * 1024, // 5MB for social banners
      disabled: uploading,
      onDrop: async (files) => {
        if (files.length > 0) {
          const file = files[0];
          setUploading(true);
          try {
            // Convert to base64 for storage
            const base64Url = await convertFileToBase64(file);
            setValue('social_banner_url', base64Url);
            toast.success("Social banner uploaded successfully! Click 'Save SEO Settings' to apply.");
          } catch (error) {
            console.error('Error converting social banner to base64:', error);
            toast.error("Failed to process social banner image");
          } finally {
            setUploading(false);
          }
        }
      },
      onDropRejected: (fileRejections) => {
        const error = fileRejections[0]?.errors[0];
        if (error?.code === 'file-too-large') {
          toast.error("Social banner file is too large. Max size is 5MB.");
        } else {
          toast.error("Invalid file type. Please upload PNG, JPG, GIF, or WebP.");
        }
      }
    });

    return (
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
        } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <input {...getInputProps()} />
        {uploading ? (
          <>
            <RefreshCw className="h-8 w-8 mx-auto mb-2 text-blue-500 animate-spin" />
            <p className="text-sm text-gray-600">Processing banner...</p>
          </>
        ) : (
          <>
            <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm text-gray-600">
              {isDragActive ? 'Drop social banner here...' : 'Drag & drop social banner or click to browse'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Recommended: 1200x630px (Facebook & Twitter optimal size)
            </p>
          </>
        )}
      </div>
    );
  };

  // Character count indicator
  const CharacterCount = ({ current, max, label }) => {
    const percentage = (current / max) * 100;
    const getColor = () => {
      if (percentage > 100) return 'text-red-600';
      if (percentage > 90) return 'text-orange-600';
      if (percentage > 75) return 'text-yellow-600';
      return 'text-green-600';
    };

    return (
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-500">{label}</span>
        <span className={getColor()}>
          {current}/{max} characters
        </span>
      </div>
    );
  };

  // SEO Preview Components
  const GooglePreview = () => (
    <div className="border rounded-lg p-4 bg-white">
      <div className="space-y-1">
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <Globe className="h-4 w-4" />
          <span>edumansystem.com</span>
        </div>
        <h3 className="text-blue-600 text-lg hover:underline cursor-pointer line-clamp-1">
          {watchedTitle || 'EduLegalHub - AI-Powered Legal & Educational Solutions'}
        </h3>
        <p className="text-gray-600 text-sm line-clamp-2">
          {watchedDescription || 'Transform legal documents and education with AI-powered solutions. Generate professional legal documents and manage educational content seamlessly.'}
        </p>
      </div>
    </div>
  );

  const FacebookPreview = () => (
    <div className="border rounded-lg overflow-hidden bg-white max-w-lg">
      {watchedBanner && (
        <div className="h-48 bg-gray-200 flex items-center justify-center">
          <img 
            src={watchedBanner} 
            alt="Social banner" 
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="hidden w-full h-full bg-gray-200 items-center justify-center">
            <Image className="h-12 w-12 text-gray-400" />
          </div>
        </div>
      )}
      <div className="p-4">
        <div className="text-xs text-gray-500 uppercase mb-1">EDULEGALHUB.PREVIEW.EMERGENTAGENT.COM</div>
        <h4 className="font-semibold text-gray-900 line-clamp-2 mb-1">
          {watchedTitle || 'EduLegalHub - AI-Powered Legal & Educational Solutions'}
        </h4>
        <p className="text-gray-600 text-sm line-clamp-2">
          {watchedDescription || 'Transform legal documents and education with AI-powered solutions.'}
        </p>
      </div>
    </div>
  );

  const TwitterPreview = () => (
    <div className="border rounded-lg overflow-hidden bg-white max-w-lg">
      {watchedBanner && (
        <div className="h-48 bg-gray-200 flex items-center justify-center">
          <img 
            src={watchedBanner} 
            alt="Social banner" 
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="hidden w-full h-full bg-gray-200 items-center justify-center">
            <Image className="h-12 w-12 text-gray-400" />
          </div>
        </div>
      )}
      <div className="p-3 border-t">
        <div className="text-xs text-gray-500 mb-1">edumansystem.com</div>
        <h4 className="font-semibold text-gray-900 line-clamp-2 text-sm">
          {watchedTitle || 'EduLegalHub - AI-Powered Legal & Educational Solutions'}
        </h4>
        <p className="text-gray-600 text-xs line-clamp-2 mt-1">
          {watchedDescription || 'Transform legal documents and education with AI-powered solutions.'}
        </p>
      </div>
    </div>
  );

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const shareUrls = generateSocialShareUrls();

  if (loading && !seoSettings) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            SEO Management
          </CardTitle>
          <CardDescription>
            Manage meta titles, descriptions, keywords, and social sharing settings
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
            <span className="ml-3 text-gray-600">Loading SEO settings...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            SEO Management
          </CardTitle>
          <CardDescription>
            Optimize your site's search engine visibility and social media sharing
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="settings" className="flex items-center gap-2">
                <Globe className="h-4 w-4" />
                SEO Settings
              </TabsTrigger>
              <TabsTrigger value="preview" className="flex items-center gap-2">
                <Eye className="h-4 w-4" />
                Preview
              </TabsTrigger>
              <TabsTrigger value="sharing" className="flex items-center gap-2">
                <Share2 className="h-4 w-4" />
                Social Sharing
              </TabsTrigger>
            </TabsList>

            {/* SEO Settings Tab */}
            <TabsContent value="settings" className="space-y-6">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Meta Title */}
                <div className="space-y-2">
                  <Label htmlFor="meta_title" className="text-base font-medium">Meta Title</Label>
                  <Input
                    id="meta_title"
                    placeholder="Enter page title (10-60 characters)"
                    {...register('meta_title')}
                    className="h-12"
                  />
                  <CharacterCount 
                    current={watchedTitle?.length || 0} 
                    max={60} 
                    label="Title length" 
                  />
                  {errors.meta_title && (
                    <p className="text-sm text-red-600">{errors.meta_title.message}</p>
                  )}
                  <p className="text-xs text-gray-500">
                    The title that appears in search results and browser tabs. Keep it compelling and under 60 characters.
                  </p>
                </div>

                {/* Meta Description */}
                <div className="space-y-2">
                  <Label htmlFor="meta_description" className="text-base font-medium">Meta Description</Label>
                  <Textarea
                    id="meta_description"
                    placeholder="Enter page description (50-160 characters)"
                    rows={3}
                    {...register('meta_description')}
                  />
                  <CharacterCount 
                    current={watchedDescription?.length || 0} 
                    max={160} 
                    label="Description length" 
                  />
                  {errors.meta_description && (
                    <p className="text-sm text-red-600">{errors.meta_description.message}</p>
                  )}
                  <p className="text-xs text-gray-500">
                    The description that appears below your title in search results. Make it informative and engaging.
                  </p>
                </div>

                {/* Meta Keywords */}
                <div className="space-y-2">
                  <Label htmlFor="meta_keywords" className="text-base font-medium">Meta Keywords</Label>
                  <Input
                    id="meta_keywords"
                    placeholder="legal documents, education, AI, teachers, lesson notes"
                    {...register('meta_keywords')}
                    className="h-12"
                  />
                  <CharacterCount 
                    current={watchedKeywords?.length || 0} 
                    max={255} 
                    label="Keywords length" 
                  />
                  {errors.meta_keywords && (
                    <p className="text-sm text-red-600">{errors.meta_keywords.message}</p>
                  )}
                  <p className="text-xs text-gray-500">
                    Comma-separated keywords relevant to your content. Focus on terms your audience searches for.
                  </p>
                </div>

                {/* Social Banner */}
                <div className="space-y-4">
                  <Label className="text-base font-medium">Social Sharing Banner</Label>
                  <BannerDropzone />
                  
                  {/* Banner Preview */}
                  {watchedBanner && (
                    <div className="border rounded-lg p-3 bg-gray-50">
                      <Label className="text-sm text-gray-600 mb-2 block">Preview:</Label>
                      <img 
                        src={watchedBanner} 
                        alt="Social banner preview" 
                        className="max-h-32 max-w-full object-contain rounded"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                      {watchedBanner?.startsWith('data:') && (
                        <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" /> Banner uploaded (will be saved as base64)
                        </p>
                      )}
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="social_banner_url">Banner URL (or upload above)</Label>
                    <Input
                      id="social_banner_url"
                      placeholder="Upload banner or enter URL"
                      {...register('social_banner_url')}
                      className={watchedBanner?.startsWith('data:') ? 'text-gray-400 text-xs' : ''}
                    />
                    {watchedBanner?.startsWith('data:') && (
                      <p className="text-xs text-gray-500">Base64 image data (uploaded via dropzone)</p>
                    )}
                    {errors.social_banner_url && (
                      <p className="text-sm text-red-600">{errors.social_banner_url.message}</p>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    Image shown when your site is shared on social media. Optimal size: 1200x630px.
                  </p>
                </div>

                <div className="flex justify-end">
                  <Button type="submit" disabled={loading} className="flex items-center gap-2">
                    {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {loading ? 'Saving...' : 'Save SEO Settings'}
                  </Button>
                </div>
              </form>
            </TabsContent>

            {/* Preview Tab */}
            <TabsContent value="preview" className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">SEO Preview</h3>
                <div className="flex gap-2">
                  <Button
                    variant={previewMode === 'google' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPreviewMode('google')}
                  >
                    Google
                  </Button>
                  <Button
                    variant={previewMode === 'facebook' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPreviewMode('facebook')}
                  >
                    Facebook
                  </Button>
                  <Button
                    variant={previewMode === 'twitter' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPreviewMode('twitter')}
                  >
                    Twitter
                  </Button>
                </div>
              </div>

              <div className="space-y-4">
                {previewMode === 'google' && (
                  <div>
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <Globe className="h-4 w-4" />
                      Google Search Results
                    </h4>
                    <GooglePreview />
                  </div>
                )}

                {previewMode === 'facebook' && (
                  <div>
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <Facebook className="h-4 w-4" />
                      Facebook Share Preview
                    </h4>
                    <FacebookPreview />
                  </div>
                )}

                {previewMode === 'twitter' && (
                  <div>
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <Twitter className="h-4 w-4" />
                      Twitter Card Preview
                    </h4>
                    <TwitterPreview />
                  </div>
                )}
              </div>

              <Alert className="border-blue-200 bg-blue-50">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  Previews show how your content will appear in search results and social media. 
                  Make sure your title is compelling and description is informative.
                </AlertDescription>
              </Alert>
            </TabsContent>

            {/* Social Sharing Tab */}
            <TabsContent value="sharing" className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Social Sharing Links</h3>
                <p className="text-gray-600">
                  Use these links to share your site on various social media platforms with optimized content.
                </p>

                <div className="grid gap-4">
                  {/* Facebook */}
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                        <Facebook className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-medium">Facebook</h4>
                        <p className="text-sm text-gray-600">Share on Facebook</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(shareUrls.facebook)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => window.open(shareUrls.facebook, '_blank')}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Twitter */}
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-400 rounded-lg flex items-center justify-center">
                        <Twitter className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-medium">Twitter</h4>
                        <p className="text-sm text-gray-600">Share on Twitter</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(shareUrls.twitter)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => window.open(shareUrls.twitter, '_blank')}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* LinkedIn */}
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-700 rounded-lg flex items-center justify-center">
                        <Linkedin className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-medium">LinkedIn</h4>
                        <p className="text-sm text-gray-600">Share on LinkedIn</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(shareUrls.linkedin)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => window.open(shareUrls.linkedin, '_blank')}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* WhatsApp */}
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                        <Share2 className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-medium">WhatsApp</h4>
                        <p className="text-sm text-gray-600">Share on WhatsApp</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(shareUrls.whatsapp)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => window.open(shareUrls.whatsapp, '_blank')}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Current SEO Status */}
              {seoSettings && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      Current SEO Status
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium">Meta Title</Label>
                        <p className="text-sm text-gray-600 truncate">{seoSettings.meta_title}</p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium">Last Updated</Label>
                        <p className="text-sm text-gray-600">
                          {new Date(seoSettings.updated_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Meta Description</Label>
                      <p className="text-sm text-gray-600 line-clamp-2">{seoSettings.meta_description}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {seoSettings.meta_keywords.split(',').slice(0, 5).map((keyword, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {keyword.trim()}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* SEO Guidelines */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5" />
            SEO Best Practices
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h4 className="font-medium">Meta Title Tips</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Keep between 10-60 characters</li>
                <li>• Include your main keyword</li>
                <li>• Make it compelling and clickable</li>
                <li>• Avoid keyword stuffing</li>
              </ul>
            </div>
            <div className="space-y-3">
              <h4 className="font-medium">Meta Description Tips</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Keep between 50-160 characters</li>
                <li>• Summarize page content clearly</li>
                <li>• Include a call-to-action</li>
                <li>• Make it unique for each page</li>
              </ul>
            </div>
            <div className="space-y-3">
              <h4 className="font-medium">Keywords Strategy</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Research your target audience</li>
                <li>• Use long-tail keywords</li>
                <li>• Include location-based terms</li>
                <li>• Monitor keyword performance</li>
              </ul>
            </div>
            <div className="space-y-3">
              <h4 className="font-medium">Social Sharing</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Use high-quality images (1200x630px)</li>
                <li>• Test how content appears on platforms</li>
                <li>• Include branded elements</li>
                <li>• Keep text readable at small sizes</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SEOManagement;